import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ProductImageMissingVisualConfig } from '@epicuro-next/ataraxia/product-image';
import {
  CellData,
  getMaterialsMatrix,
} from '@epicuro-next/features/config-strip/utilities/materials';
import { maxUnlimitedQuantityOrder } from '@epicuro-next/platform/configuration';
import {
  ActiveProductPricesDic,
  CellInventory,
  DeliveryItems,
} from '@epicuro-next/state/config-strip-state';
import {
  CustomerProductIdentifierUpdate,
  ProductExtensionEntities,
} from '@epicuro-next/state/customer-product-identifiers-state';
import { ProductListActionsBaseComponentStore } from '@epicuro-next/state/product-list-state';
import { Dictionary } from '@ngrx/entity';
import {
  BasketAccountAddress,
  DeliveryWindowType,
} from 'graphql-api/baskets-api-interfaces';
import {
  DabData,
  DabSize,
  OrderType,
  Product,
  ScaleSets,
} from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  pairwise,
  takeUntil,
} from 'rxjs/operators';

import { getCellInventoryTotals } from '../../shared/cell/cell-inventory';
import { QuantityChangedPayload, UpdateTotalsPayload } from '../../types';

export interface CellPosition {
  materialId?: string;
  row?: number;
  column?: number;
}

@Directive()
export class BaseSizeGridComponent implements OnChanges, OnDestroy {
  public get scaleSelectorVisible() {
    return this.selectedScale && this.scaleSets && this.scaleSets?.length > 1;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    protected cdr: ChangeDetectorRef,
    public componentStore: ProductListActionsBaseComponentStore,
  ) {}

  public readonly maxUnlimitedQuantityOrder = maxUnlimitedQuantityOrder;
  public readonly min = Math.min;

  /** array of products to be shown in the grid */
  @Input() public materials: Product[] = [];
  /** array of material ids that are not available in the grid */
  @Input() public disabledMaterialsIds: {
    [key: string]: { hasNow: boolean; dates: string[]; future: string };
  };

  /** scale sets for the materials */
  @Input()
  public availableScaleSets: ScaleSets;

  /** scale sets for scale set selector */
  @Input()
  public scaleSets: string[];

  /** selected scale from the scale sets */
  @Input()
  public selectedScale: string;

  /** to distinguish selected value and display property of scales */
  @Input()
  public scaleDisplayObject: { [key: string]: string };

  /** items already entered by the user for the currently active delivery date */
  @Input()
  public deliveryItems: DeliveryItems;

  /** account address string used to react to the store changes */
  @Input()
  public deliveryShop: BasketAccountAddress;

  @Input()
  public deliveryWindowType: DeliveryWindowType;

  @Input()
  public showNextAvailableDate = false;

  /** available inventory items by sizeId */
  @Input()
  public sizesInventories: { [sizeId: string]: CellInventory } = {};

  @Input()
  public cellsQuantities: DeliveryItems;

  @Input()
  public showTotals = true;

  @Input()
  public readonly = false;

  @Input()
  public preSelectedMaterial?: string;

  @Input()
  public dabsData: Dictionary<DabData[]> = {};

  @Input()
  public productExtensions: ProductExtensionEntities = {};

  @Input()
  public editCustomerProductIdentifierEnabled = false;

  @Input()
  public isCopyPrefilledVisible = false;

  @Input()
  public isScaleSelectorDisabled = false;

  @Input() public titleAttributes: string[];

  @Input() public materialsPrices: ActiveProductPricesDic = {};

  // controls visibility of bulk badges for cells and material row
  @Input()
  public isNowOrder = false;

  @Input()
  public showInventoryOverview = true;

  @Input()
  public skipInventoryCheck = false;

  @Input() public differentMaterialDimensions = false;

  @Input()
  public sizerunsGrid = false;

  @Input()
  public sizeRunsSizes: number[] = [];

  @Input()
  public productImageMissingVisualConfig: ProductImageMissingVisualConfig;

  @Output()
  public clickedMaterialImage = new EventEmitter<string>();

  @Output()
  public customerProductIdentifierUpdate = new EventEmitter<CustomerProductIdentifierUpdate>();

  /** Event dispatched when a material is selected */
  @Output() public sizeSelect = new EventEmitter<Partial<CellData>>();
  /** Event dispatched when a quantity for a specific size changes */
  @Output() public quantityChange = new EventEmitter<
    QuantityChangedPayload[]
  >();
  /** Event dispatched when quantities should be preserved and re-used */
  @Output() public masterQuantitiesPrefill = new EventEmitter<string>();
  /** Event dispatched when the scale selector changes */
  @Output() public scaleSelected = new EventEmitter<string>();

  @Output() public periodSelected = new EventEmitter<string>();
  /** total values for the columns, to be shown in the totals column */
  public colTotals = signal<number[]>([]);
  /** total values for the rows, to be shown in the totals footer row */
  public rowTotals = signal<{ [materialId: string]: number[] }>({});

  /** total values for the columns, to be shown in the totals column */
  public trueDemandColTotals = signal<number[]>([]);
  /** total values for the rows, to be shown in the totals footer row */
  public trueDemandRowTotals = signal<{ [materialId: string]: number[] }>({});

  /** Gives current default product id from where quantities will be copied */
  public masterQuantitiesPrefillProductId?: string | null;
  /** identifies the currently selected cell */
  public activeCell: CellPosition;
  /** currently selected scale */
  public activeScale: { [key: string]: string[] };

  /** Array of sizes to show as row headers */
  public ySizes: string[];
  /** Array of sizes to show as column headers */
  public xSizes: string[];

  /** Map of material id and matrix of sizes. */
  protected materialsMatrix = new Map<string, [CellData[]]>();

  public formGroup: UntypedFormGroup;
  protected destroy$ = new Subject<void>();
  protected buildingForm$ = new Subject<void>();

  protected activeRow: { material: string; row: number } | null;

  /** identifies the currently souce copy row */
  protected copyActiveRow: { material: string; row: number } | null;

  public readonly inboundTitle = $localize`:ConfigStrip|Label for inbound quantity badge@@inboundQuantityBadgeLabel:Inbound quantity`;
  public readonly lockedTitle = $localize`:ConfigStrip|Label for locked badge@@materialLockedBadgeLabel:This material is locked`;

  public materialInboundToShow: string;

  /** Is needed to differentiate user first input and items received from basket
   * shall be deleted and `ngOnChanges` and `refreshData` re-written
   */
  protected isUserEnteredQuantity = false;
  protected sizeRunRefreshPending$$ = new BehaviorSubject(true);
  protected deliveryItemsVoid$$ = new Subject<void>();
  protected formControlsConfig: object;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.materials || changes.sizesInventories || changes.deliveryShop) {
      this.refreshData();
    } else if (changes.selectedScale) {
      this.selectScale(this.selectedScale);
    }
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected refreshData() {
    if (this.formControlsConfig)
      this.formGroup.reset(this.formControlsConfig, { emitEvent: false });

    this.processMaterials();
    this.selectScale(this.selectedScale);
    this.buildForm();
  }

  public getInboundQuantity(productId: string) {
    return this.dabsData[productId]?.[0]?.inboundItemsQuantity?.[0] ?? 0;
  }

  public toggleInboundQuantity(materialId: string, shouldShow: boolean) {
    this.materialInboundToShow = shouldShow ? materialId : '';
  }

  public onMasterQuantitiesPrefill({ productId }: Product) {
    this.masterQuantitiesPrefillProductId = productId;
    this.masterQuantitiesPrefill.emit(productId);
  }

  /**
   * Creates the form structure
   */
  protected buildForm() {
    this.buildingForm$.next();
    const formControlsConfig: { [materialId: string]: UntypedFormArray } = {};
    const formData: { [materialId: string]: CellData[][] } = {};

    this.materials.forEach(({ productId }) => {
      const materialData = this.materialsMatrix.get(productId) ?? [[]];

      if (this.ySizes) {
        for (let yCurrent = 0; yCurrent < this.ySizes.length; yCurrent++) {
          if (!materialData[yCurrent]) {
            materialData[yCurrent] = new Array();
          }
        }
      }

      formControlsConfig[productId] = this.buildRowCellGroups(materialData);
      this.formControlsConfig = formControlsConfig;

      formData[productId] = materialData;
    });

    this.formGroup = this.fb.group(formControlsConfig);
    this.formGroup.patchValue(formData);
  }

  /**
   * Create the cell group components for a row
   * @param materialData
   * @param rowArray
   */
  protected buildRowCellGroups(materialData?: [CellData[]]) {
    const rowArray = this.fb.array([]);

    if (materialData) {
      materialData.forEach((colData: CellData[], row) => {
        const colArray = this.fb.array([]);
        this.xSizes.forEach((_, col) => {
          colArray.push(this.createCellGroup(row, col, colData[col]));
        });
        rowArray.push(colArray);
      });
    }

    return rowArray;
  }

  protected createCellGroup(row: number, col: number, data?: CellData) {
    const control = this.fb.control(
      {
        value: data?.quantity,
        disabled: !data || data.isLocked,
      },
      [
        Validators.min(0),
        Validators.max(
          data
            ? this.sizesInventories[data.productId]?.totals ||
                Number.MAX_SAFE_INTEGER
            : 0,
        ),
      ],
    );

    if (data) {
      this.subscribeToControlChanges(control, row, col, data);
    }

    return this.fb.group({
      quantity: control,
    });
  }

  protected subscribeToControlChanges(
    formControl: UntypedFormControl,
    row: number,
    col: number,
    data: CellData,
  ) {
    formControl.valueChanges
      .pipe(
        debounceTime(300),
        map((newVal) => {
          if (this.skipInventoryCheck) {
            return newVal ?? 0;
          }

          const inventory = getCellInventoryTotals(
            this.materialsMatrix,
            this.sizesInventories,
          )(data.materialId, row, col);

          const cellData = this.materialsMatrix.get(data.materialId)?.[row]?.[
            col
          ];
          const hasTrueDemand = cellData
            ? this.sizesInventories[cellData.productId]?.hasTrueDemandInventory
            : false;

          if (inventory && (newVal ?? 0) > inventory && !hasTrueDemand) {
            return inventory;
          }
          return newVal ?? 0;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        takeUntil(this.buildingForm$),
        pairwise(),
      )
      .subscribe(([prevVal, newVal]) => {
        const newValue = newVal !== '' ? newVal : 0;
        const prevValue = prevVal !== '' ? prevVal : 0;
        const diff = newValue - prevValue;
        if (isNaN(diff)) {
          return;
        }

        this.isUserEnteredQuantity = true;
        this.sizeRunRefreshPending$$.next(false);
        this.quantityChange.emit([{ ...data, quantity: newValue }]);

        if (data.materialId === this.masterQuantitiesPrefillProductId) {
          this.masterQuantitiesPrefill.emit(data.materialId);
        }

        this.cdr.markForCheck();
      });
  }

  public updateTotals(totals: UpdateTotalsPayload) {
    const { materialId, row, col, diff } = totals;
    this.updateTotalsForColumn(col, diff);
    this.updateTotalsForRow(materialId, row, diff);
  }

  public updateTrueDemandTotals(totals: UpdateTotalsPayload) {
    const { materialId, row, col, diff } = totals;
    if (materialId) {
      const totalRows =
        this.trueDemandRowTotals()[materialId] || ([] as number[]);
      totalRows[row] = (totalRows[row] || 0) + diff;
      this.trueDemandRowTotals.set({
        ...this.trueDemandRowTotals(),
        [materialId]: totalRows,
      });
    }

    this.trueDemandColTotals.update((colTotals) => {
      colTotals[col] = (colTotals[col] || 0) + diff;
      return [...colTotals];
    });
  }

  protected updateTotalsForRow(materialId: string, row: number, diff: number) {
    /* istanbul ignore else */
    if (materialId) {
      const totalRows = this.rowTotals()[materialId] || ([] as number[]);
      totalRows[row] = (totalRows[row] || 0) + diff;
      this.rowTotals.set({
        ...this.rowTotals(),
        [materialId]: totalRows,
      });
    }
  }

  /**
   * Update the object used for the row of totals
   * @param col
   * @param diff
   */
  protected updateTotalsForColumn(col: number, diff: number) {
    this.colTotals.update((totals) => {
      totals[col] = (totals[col] || 0) + diff;
      return [...totals];
    });
  }

  /**
   * sets the active scale and fills the xSizes and ySizes for the grid.
   * @param scale
   */
  public selectScale(scale: string) {
    this.activeScale = this.availableScaleSets.scales[scale];
    // TODO - BE-1262 Have a way to get consistent dimensions.
    // For Bras it might be band / cup, for shoes might be size / width, for jeans size / length.
    // There's currently no way to figure out which one is the main dimension.
    const [xDimension, yDimension] = Object.keys(this.activeScale);
    this.xSizes = this.activeScale[xDimension];
    this.ySizes = this.activeScale[yDimension];
  }

  /**
   * Tracks elements by its index
   * @param index
   */
  public trackByIndex(index: number) {
    return index;
  }

  /**
   * Tracks by the productId attribute
   * @param _
   * @param product
   */
  public trackByProductId(_: number, product: Product) {
    return product.productId;
  }

  public getMaterialDiscount(materialId: string) {
    return this.materialsPrices?.[materialId]?.discount;
  }

  public getRowInventorySum(materialId: string, row: number) {
    const materialData = this.materialsMatrix.get(materialId);

    return (
      materialData?.[row]?.reduce((acc, cellData) => {
        if (this.sizesInventories[cellData.productId]?.hasTrueDemandInventory) {
          return Number.POSITIVE_INFINITY;
        }
        return acc + this.sizesInventories[cellData.productId]?.totals;
      }, 0) ?? 0
    );
  }

  public getCellQuantity(materialId: string, row: number, col: number) {
    const materialData = this.materialsMatrix.get(materialId);
    return materialData?.[row]?.[col]?.quantity ?? 0;
  }

  private findFirstAvailableSize(materialId: string) {
    return this.materialsMatrix.get(materialId)?.reduce(
      (aggr, rowData, row) => {
        const column = rowData.findIndex(
          (_, columnIndex) =>
            getCellInventoryTotals(this.materialsMatrix, this.sizesInventories)(
              materialId,
              row,
              columnIndex,
            ) > 0,
        );
        if (column !== -1) {
          return { row, column };
        }
        return aggr;
      },
      { row: 0, column: 0 },
    );
  }

  public hasFastDelivery(productId: string) {
    const productMatrix = this.materialsMatrix.get(productId);

    if (productMatrix) {
      return productMatrix.some((row) =>
        row.some((cell) => cell.isPreferredNoosWarehouse),
      );
    }
    return false;
  }

  /**
   * Emits material click event.
   * @param materialId
   */
  public onMaterialClick(materialId: string) {
    const firstAvailableSize = this.findFirstAvailableSize(materialId);
    this.selectCell(
      materialId,
      firstAvailableSize?.row ?? 0,
      firstAvailableSize?.column ?? 0,
    );
  }

  /**
   * Stores the material index, row and column to which the cell belongs
   * @param materialId
   * @param row
   * @param column
   */
  public selectCell(materialId: string, row: number, column: number) {
    this.preSelectedMaterial = undefined;

    this.activeCell = { materialId, row, column };
    this.activeRow = { material: materialId, row };
    this.copyActiveRow = { material: materialId, row };

    const materialData = this.materialsMatrix.get(materialId);
    const productId = materialData && materialData[row][column]?.productId;
    // only for typing, it's not the case that productId would be undefined but
    // providing "!" above is not safer either
    this.sizeSelect.emit({ productId: productId ?? '', materialId });

    this.cdr.markForCheck();
  }

  /**
   * Returns the matrix for the material
   * @param material material productId
   */
  // @deprecated use `this.materialsMatrix.get(material);` instead
  public getMaterialData(material: string) {
    return this.materialsMatrix.get(material);
  }

  public getSizeId(materialId: string, row: number, column: number) {
    return this.materialsMatrix.get(materialId)?.[row]?.[column]?.productId;
  }

  public getDabForSize(
    materialId: string,
    row: number,
    column: number,
  ): string | null {
    const sizeId = this.getSizeId(materialId, row, column);
    if (!sizeId) {
      return null;
    }
    return (
      this.dabsData[materialId]?.[0]?.inboundSizes
        ?.find(({ productId }: DabSize) => productId === sizeId)
        ?.quantity?.toString() ?? null
    );
  }

  public getDabTotalForMaterial(materialId: string, row: number) {
    return this.materialsMatrix
      .get(materialId)
      ?.[row].reduce((total: number, cell: CellData) => {
        const cellTotal =
          this.dabsData[materialId]?.[0]?.inboundSizes?.find(
            ({ productId }: DabSize) => productId === cell.productId,
          )?.quantity ?? 0;

        total += cellTotal;

        return total;
      }, 0);
  }

  public clickMaterialImage(variantId: string) {
    this.clickedMaterialImage.emit(variantId);
  }

  public selectNow() {
    this.periodSelected.emit(OrderType.Now);
  }

  public selectPeriod(period: string) {
    this.periodSelected.emit(period);
  }

  public getMaterialTitle(material: Product): string {
    return this.titleAttributes
      ?.map((att) =>
        att.split('.').reduce((ac: { [x: string]: unknown }, c: string) => {
          return ac[c];
        }, material),
      )
      .filter((item) => !!item)
      .join(' ');
  }

  public getCustomerProductIdentifier(productId: string) {
    return this.productExtensions[productId]?.customerProductIdentifier ?? '';
  }

  /**
   * Process the materials to create a matrix of sizes.
   * A material can have sizes with 1 or 2 dimensions, which are defined in the scale sets.
   */
  protected processMaterials() {
    this.materialsMatrix = getMaterialsMatrix(
      this.availableScaleSets,
      this.materials,
      this.deliveryItems,
      this.differentMaterialDimensions,
    );
  }

  /**
   * Returns the position of the dimension in the scale sets map.
   * @param dimension
   * @param sizeProduct
   */
  public handleScaleSelection(scaleSet: string) {
    this.scaleSelected.emit(scaleSet);
  }

  protected getDimensionIndex(dimension: string, sizeProduct: Product) {
    const defaultScaleSets =
      this.availableScaleSets.scales[this.availableScaleSets.default];
    const dimensionValue = sizeProduct.attributes[dimension] as string;
    return defaultScaleSets[dimension].indexOf(dimensionValue);
  }

  protected isCellHasConsignment(
    productId: string,
    row: number,
    column: number,
  ) {
    const productMatrix = this.materialsMatrix.get(productId);
    if (productMatrix) {
      return productMatrix[row][column]?.isConsignmentAvailable || false;
    }
    return false;
  }

  protected isRowHasConsignment(productId: string) {
    const productMatrix = this.materialsMatrix.get(productId);
    if (productMatrix) {
      return productMatrix.some((row) =>
        row.some((cell) => cell.isConsignmentAvailable),
      );
    }
    return false;
  }

  protected isAnyRowHasConsignment(materials: Product[]) {
    return materials.some((item) => this.isRowHasConsignment(item.productId));
  }

  public getSizeRunsQuantity(materialId: string) {
    try {
      const rowData = (this.getMaterialData(materialId) ?? [])[0];

      const quantities =
        rowData?.map((item) => (item.quantity ? item.quantity : 0)) ?? [];
      const quantitiesRatio = quantities?.map((item, index) =>
        Math.ceil(item / (this.sizeRunsSizes[index] || 1)),
      );
      const max = Math.max.apply(Math, quantitiesRatio);

      return Number.isFinite(max) ? max : 0;
    } catch {
      return 0;
    }
  }

  public getSizeRunsQuantitiesMax(materialId: string) {
    try {
      const material = this.materialsMatrix.get(materialId);

      if (!material) {
        return maxUnlimitedQuantityOrder;
      }

      const totals: number[] = material[0].map((item) => {
        return this.sizesInventories[item.productId].totals;
      });

      const maxAll = this.sizeRunsSizes.map((item, index) => {
        return Math.ceil((totals[index] ?? 0) / (item ? item : 1));
      });
      const max = Math.max.apply(Math, maxAll);

      return Number.isFinite(max) ? max : maxUnlimitedQuantityOrder;
    } catch {
      return maxUnlimitedQuantityOrder;
    }
  }

  public hasSomeNotFulfilled(materialId: string, row: number) {
    const materialMatrix = this.materialsMatrix?.get(materialId);
    const sizeRunsMultiplier = this.getSizeRunsQuantity(materialId);

    if (!materialMatrix || !sizeRunsMultiplier) {
      return false;
    }

    return this.sizeRunsSizes.some((size, index) => {
      const rowItems = materialMatrix[row] ?? [];
      const colItem: CellData = rowItems[index] ?? {};

      const currentQuantity =
        this.cellsQuantities?.items?.[colItem?.productId]?.quantity ?? 0;
      const expectedSizeRunsColumnValue = size * (sizeRunsMultiplier || 1);

      return expectedSizeRunsColumnValue > currentQuantity;
    });
  }

  public isRequestedMoreThenAvailable(
    materialId: string,
    row: number,
    column: number,
  ) {
    const materialMatrix = this.materialsMatrix?.get(materialId);
    const sizeRunsMultiplier = this.getSizeRunsQuantity(materialId);

    if (!materialMatrix || !sizeRunsMultiplier) {
      return false;
    }
    const currentQuantity = this.getCellQuantity(materialId, row, column) ?? 0;
    const expectedSizeRunsColumnValue =
      this.sizeRunsSizes[column] * (sizeRunsMultiplier || 1);

    return expectedSizeRunsColumnValue > currentQuantity;
  }
  public getInBasketQuantity(materialId: string, row: number, column: number) {
    const materialMatrix = this.materialsMatrix?.get(materialId);
    if (!materialMatrix) return 0;
    return materialMatrix[row]?.[column]?.quantity ?? 0;
  }
}
